'use client';

/**
 * ExploreClient — category filter bar for the Explore page.
 *
 * Reads/writes the `?category=` search param to let the server page
 * re-render with filtered results. Uses Next.js router.push so the
 * server component re-runs on the server with the new param.
 */

import { useRouter, useSearchParams } from 'next/navigation';
import { Film, Tv, BookOpen, Mic2, UtensilsCrossed, MapPin, LayoutGrid } from 'lucide-react';
import { cn } from '@/lib/utils';

const FILTERS = [
  { label: 'All',         slug: null,          icon: LayoutGrid },
  { label: 'Films',       slug: 'movie',        icon: Film },
  { label: 'TV',          slug: 'tv-show',      icon: Tv },
  { label: 'Books',       slug: 'book',         icon: BookOpen },
  { label: 'Restaurants', slug: 'restaurant',   icon: UtensilsCrossed },
  { label: 'Places',      slug: 'place',        icon: MapPin },
  { label: 'Podcasts',    slug: 'podcast',      icon: Mic2 },
] as const;

export function ExploreCategoryFilter() {
  const router = useRouter();
  const searchParams = useSearchParams();
  const current = searchParams.get('category');

  function select(slug: string | null) {
    const params = new URLSearchParams(searchParams.toString());
    if (slug) {
      params.set('category', slug);
    } else {
      params.delete('category');
    }
    router.push(`/explore?${params.toString()}`);
  }

  return (
    <div className="flex gap-2 overflow-x-auto pb-1 scrollbar-none -mx-4 px-4 md:mx-0 md:px-0 md:flex-wrap">
      {FILTERS.map(({ label, slug, icon: Icon }) => {
        const active = slug === current || (slug === null && !current);
        return (
          <button
            key={label}
            onClick={() => select(slug)}
            className={cn(
              'flex items-center gap-1.5 whitespace-nowrap rounded-full border-2 px-3 py-1.5 text-sm font-semibold transition-all shrink-0',
              active
                ? 'border-primary bg-primary text-primary-foreground shadow-[2px_2px_0_var(--primary)]'
                : 'border-border bg-card text-muted-foreground hover:border-primary/40 hover:text-foreground'
            )}
          >
            <Icon className="h-3.5 w-3.5" strokeWidth={2} />
            {label}
          </button>
        );
      })}
    </div>
  );
}
